import { Form, Link, useActionData, useLoaderData, useNavigation, useParams, useRouteLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { Change, DecisionProposal, Review, Significance } from "@edm/contracts";
import { KeyValueSkeleton, Lines, Loading, times } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { translate, translatePlural, useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { ArrowLeftIcon, Badge, Button, Callout, Card, FormError, KeyValue, PageHeader, Skeleton, StatusBadge } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

// Architecture-aware review (ENG-19). The point of this page is the correction
// loop: a reviewer confirms what matters, dismisses what does not, and the
// dismissal sticks. Findings are never presented as verdicts.

type Finding = Review["findings"][number];

const tone = (f: Finding) =>
  f.class === "conflict" ? (f.validationMode === "deterministic" ? "danger" : "warning") : f.class === "conformance" ? "success" : "info";

// The change list page is gone: changes are reached from My Changes, from an
// Inbox finding, or from a decision's lineage. This file keeps the detail page,
// which is what those links point at.

type ChangeRecord = { change: Change; review: Review; significance: Significance | null };

export function changeLoader({ context, params }: LoaderFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const record = Promise.all([api.getChange(params.id!), api.changeReview(params.id!), api.changeSignificance(params.id!).catch(() => null)]).then(
    ([change, review, significance]) => ({ change, review, significance }) as ChangeRecord,
  );
  return { record: deferred(record) };
}

export async function changeAction({ context, params, request }: ActionFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const form = await request.formData();
  const intent = String(form.get("intent") ?? "");
  try {
    if (intent === "propose") {
      const proposal = (await api.proposeDecision(params.id!, form.get("force") === "true")) as DecisionProposal;
      if (!proposal.decisionId) return { ok: translate("change.proposal.notNeeded") };
      return {
        ok: proposal.created
          ? translate("change.proposal.drafted", { id: proposal.decisionId })
          : translate("change.proposal.exists", { id: proposal.decisionId }),
      };
    }
    if (intent === "review") {
      const review = await api.runReview(params.id!);
      return { ok: translatePlural("change.reviewed", review.findings.length, { revision: review.revision ?? "" }) };
    }
    await api.resolveFinding(String(form.get("findingId")), intent as "confirm" | "dismiss" | "accept_exception", String(form.get("reason") ?? "") || undefined);
    return {
      ok:
        intent === "dismiss"
          ? translate("change.dismissed")
          : intent === "accept_exception"
            ? translate("change.exceptionRecorded")
            : translate("change.confirmed"),
    };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

function FindingCard({ finding, busy, base }: { finding: Finding; busy: boolean; base: string }) {
  const { t, tOr } = useI18n();
  return (
    <Card title={finding.title} data-testid="finding">
      <div className="dt-inline dt-gap-2" style={{ flexWrap: "wrap" }}>
        <Badge tone={tone(finding)}>{tOr(`findingClass.${finding.class}`, finding.class.replaceAll("_", " "))}</Badge>
        <Badge tone={finding.validationMode === "deterministic" ? "neutral" : "info"}>
          {finding.validationMode === "deterministic" ? t("finding.verified") : t("finding.readFromDescription")}
        </Badge>
        <span className="dt-subtle">
          {t("finding.meta", { severity: tOr(`severity.${finding.severity}`, finding.severity), confidence: Math.round(finding.confidence * 100) })}
        </span>
        {finding.status === "confirmed" && <StatusBadge status="accepted" />}
      </div>
      <p>{finding.explanation}</p>
      {(finding.decisionIds?.length || finding.constraintIds?.length) && (
        <p className="dt-subtle">
          {t("finding.basedOn")}{" "}
          {(finding.decisionIds ?? []).map((id) => (
            <Link key={id} to={`${base}/decisions/${id}`} className="dt-mono">
              {id}{" "}
            </Link>
          ))}
          {(finding.constraintIds ?? []).map((id) => (
            <code key={id} className="dt-mono">
              {id}{" "}
            </code>
          ))}
        </p>
      )}
      {finding.locations?.length ? <p className="dt-mono dt-subtle">{finding.locations.slice(0, 5).join(", ")}</p> : null}
      {finding.suggestedActions?.map((s) => (
        <p key={s} className="dt-muted">
          {s}
        </p>
      ))}
      {finding.id && finding.status !== "confirmed" && (
        <div className="dt-inline dt-gap-2" style={{ flexWrap: "wrap" }}>
          {(["confirm", "dismiss", "accept_exception"] as const).map((outcome) => (
            <Form method="post" key={outcome}>
              <input type="hidden" name="intent" value={outcome} />
              <input type="hidden" name="findingId" value={finding.id} />
              <Button type="submit" size="sm" variant={outcome === "confirm" ? "primary" : "ghost"} loading={busy}>
                {outcome === "confirm" ? t("finding.confirm") : outcome === "dismiss" ? t("finding.falsePositive") : t("finding.acceptedException")}
              </Button>
            </Form>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Back to My Changes, then the change's facts and its findings. The id is known from the URL. */
export function ChangeSkeleton() {
  const { t } = useI18n();
  const { workspaceSlug, id } = useParams();
  return (
    <Loading>
      <PageHeader
        eyebrow={
          <>
            <Link to={`/w/${workspaceSlug}/my-changes`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
              <ArrowLeftIcon /> {t("nav.myChanges")}
            </Link>
            <span aria-hidden="true">/</span>
            <code>{id}</code>
          </>
        }
        title={<Skeleton width={420} />}
        description={<Skeleton width={320} />}
      />
      <div className="dt-article">
        <Card>
          <KeyValueSkeleton keys={[t("change.kv.status"), t("change.kv.scope"), t("change.kv.references"), t("change.kv.components")]} />
          <Skeleton shape="button" width={120} />
        </Card>
        {times(2).map((i) => (
          <Card key={i} title={<Skeleton width={i ? 200 : 260} />}>
            <div className="dt-inline dt-gap-2 dt-align-center">
              <Skeleton shape="badge" width={80} />
              <Skeleton shape="badge" width={120} />
              <Skeleton width={140} />
            </div>
            <Lines count={2} />
          </Card>
        ))}
      </div>
    </Loading>
  );
}

export function ChangeRoute() {
  const { record } = useLoaderData<typeof changeLoader>();
  return (
    <Deferred data={record} fallback={<ChangeSkeleton />}>
      {(record) => <ChangePage {...record} />}
    </Deferred>
  );
}

function ChangePage({ change, review, significance }: ChangeRecord) {
  const result = useActionData() as { ok?: string; error?: string } | undefined;
  const busy = useNavigation().state === "submitting";
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t, tOr, tPlural } = useI18n();
  const base = `/w/${workspace.slug}`;
  const material = review.findings.filter((f) => f.class === "conflict" || f.class === "ambiguity");
  const context = review.findings.filter((f) => !material.includes(f));
  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to={`${base}/my-changes`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
              <ArrowLeftIcon /> {t("nav.myChanges")}
            </Link>
            <span aria-hidden="true">/</span>
            <code>{change.id}</code>
          </>
        }
        title={change.title}
        description={change.intent}
      />
      <div className="dt-article">
        <FormError message={result?.error} />
        {result?.ok && <Callout tone="success">{result.ok}</Callout>}
        <Card>
          <KeyValue
            items={[
              { key: t("change.kv.status"), value: tOr(`status.${change.status}`, change.status.replaceAll("_", " ")) },
              { key: t("change.kv.scope"), value: change.scope || t("common.none") },
              { key: t("change.kv.references"), value: (change.references ?? []).join(" · ") || t("common.none") },
              {
                key: t("change.kv.components"),
                value: review.components.length
                  ? review.components
                      .map((c) =>
                        t("change.component", {
                          name: c.name,
                          files: tPlural("change.componentFiles", c.files),
                          provenance: tOr(`provenance.${c.provenance}`, c.provenance.replace("_", " ")),
                        }),
                      )
                      .join(", ")
                  : t("change.components.none"),
              },
            ]}
          />
          <Form method="post">
            <input type="hidden" name="intent" value="review" />
            <Button type="submit" variant="secondary" loading={busy}>
              {t("change.reviewAgain")}
            </Button>
          </Form>
        </Card>
        {significance && (
          <Card title={t("change.significance.title")} data-testid="significance">
            <div className="dt-inline dt-gap-2" style={{ flexWrap: "wrap" }}>
              <Badge tone={significance.decisionWorthy ? "warning" : "neutral"}>
                {significance.decisionWorthy ? t("change.significance.worthy") : t("change.significance.routine")}
              </Badge>
              <span className="dt-subtle">{significance.summary}</span>
            </div>
            {(significance.signals ?? []).map((s) => (
              <p key={s.rule} className="dt-subtle">
                {s.detail}
              </p>
            ))}
            {(significance.supersedes ?? []).length > 0 && (
              <p className="dt-muted">{t("change.significance.supersedes", { ids: (significance.supersedes ?? []).join(", ") })}</p>
            )}
            <Form method="post">
              <input type="hidden" name="intent" value="propose" />
              {!significance.decisionWorthy && <input type="hidden" name="force" value="true" />}
              <Button type="submit" variant="secondary" loading={busy}>
                {t("change.proposal.draft")}
              </Button>
            </Form>
          </Card>
        )}
        {review.findings.length === 0 ? (
          <EmptyState title={t("change.noFindings.title")} hint={t("change.noFindings.hint")} />
        ) : (
          <>
            {material.map((f) => (
              <FindingCard key={f.fingerprint} finding={f} busy={busy} base={base} />
            ))}
            {context.map((f) => (
              <FindingCard key={f.fingerprint} finding={f} busy={busy} base={base} />
            ))}
          </>
        )}
        {review.uncertainty?.insufficientEvidence && (
          <Callout tone="warning" title={t("change.uncertainty.title")}>
            {t("change.uncertainty.body")}
          </Callout>
        )}
      </div>
    </>
  );
}
